import { useMemo, useEffect } from 'react';
import { useInfiniteQuery, useQueryClient, Updater, UseInfiniteQueryResult } from '@tanstack/react-query';
import { ChatPageMessageListQueryKeys } from '@enums/react-query-keys';
import { usePreferences } from '@context/preferences.context';
import { useChat } from '@context/chat.context';
import logger from '@helpers/utils/logger/client';

const useChatHistory = (limit: number) => {
  const {
    channel,
    channelId,
    translateMessagePromise,
    setIsLoading,
    setMessages,
    transformMessages,
    messages,
    mode,
    chatWrapper,
  } = useChat();
  const { language } = usePreferences();
  const queryClient = useQueryClient();

  const { data, isFetching, isFetchingNextPage, fetchNextPage, hasNextPage } = useInfiniteQuery({
    queryKey: [ChatPageMessageListQueryKeys.CHAT_PAGE_CHAT_HISTORY, channelId],
    enabled: !!channel && mode === 'inline',
    queryFn: ({ pageParam = null }) => {
      return channel?.query({ messages: { limit, id_lt: pageParam } });
    },
    getNextPageParam: (lastPage) => {
      if (lastPage.messages.length < limit) {
        return undefined;
      }
      return lastPage && lastPage.messages.length > 0 ? lastPage.messages[0].id : undefined;
    },
  });

  const originalMessages = useMemo(() => data?.pages.reduce((t, c) => [...c.messages, ...t], []), [data]);

  useEffect(() => {
    if (!originalMessages || !originalMessages.length) {
      return;
    }
    const messagePromises = originalMessages.map((msg) => {
      return msg.i18n && msg.i18n[`${language}_text`]
        ? Promise.resolve(msg)
        : translateMessagePromise(msg.id, language).catch(() => msg);
    });
    Promise.all(messagePromises)
      .then((msgs) => {
        msgs.forEach((message) => {
          message.status = 'received';
        });
        const newMsgs = transformMessages(msgs);
        setMessages(newMsgs);
      })
      .catch((err) => {
        const newMsgs = transformMessages(originalMessages);
        setMessages(newMsgs);
        logger.error(err);
      })
      .finally(() => {
        setIsLoading(false);
        const lastIndexCurpage = data?.pages[data?.pages.length - 1]?.messages?.length - 1;
        const element = chatWrapper.current.querySelectorAll('.msgChat__item')[lastIndexCurpage] as HTMLDivElement;
        if (element) {
          chatWrapper.current.scrollTop = element.offsetTop + element.offsetHeight;
        }
      });
  }, [originalMessages]);

  const updater = (fn: Updater<UseInfiniteQueryResult, UseInfiniteQueryResult>) => {
    queryClient.setQueryData([ChatPageMessageListQueryKeys.CHAT_PAGE_CHAT_HISTORY, channelId], fn);
  };

  return {
    messages,
    isFetching,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
    updater,
  };
};

export default useChatHistory;
